/**
 * What the Intercom workspace accepts on an attachment, lowercased and without the dot.
 *
 * A mirror of an Intercom setting (Settings > Security > Receiving attachments), not a policy of
 * ours. It is repeated here — the authority is `services/api/pkg/intercom/attachments.go` — because
 * Intercom rejects a disallowed extension with a 403 that fails the ENTIRE reply, message and all.
 * Catching it before the upload is the difference between "server.jar is not a supported file type"
 * and a generic send error the customer can only answer by trying again.
 *
 * NOT here, and not addable: Intercom permanently blocks a set of extensions that no workspace
 * setting overrides — `jar`, `bat`, `exe`, `dll`, `js`, `msi`, `iso`, `vbs`, `webp` among them.
 * Two of those matter for us: `jar`, because Minecraft plugins and mods are jars and customers have
 * to zip them, and `webp`, because browsers produce it without being asked.
 *
 * Keep in step with the Go list. Adding one here without the workspace and the backend only moves
 * the failure later.
 */
export const ALLOWED_ATTACHMENT_EXTENSIONS: ReadonlySet<string> = new Set([
  // Intercom defaults.
  'gif',
  'jpeg',
  'jpg',
  'mov',
  'mp4',
  'pdf',
  'png',
  'txt',
  'heic',
  'oga',
  'ogg',
  'dng',
  'json',

  // Logs and configuration — the most common attachment on a game-server ticket.
  'log',
  'cfg',
  'ini',
  'conf',
  'properties',
  'yml',
  'yaml',
  'toml',
  'xml',
  'csv',

  // Archives: world saves, modpacks, bundled logs — and the workaround for `jar`.
  'zip',
  '7z',
  'rar',
  'tar',
  'gz',
  'tgz',

  // Saves and data.
  'sav',
  'dat',
  'db',
  'sqlite',

  // Game bundles. Zip-based, none blocked.
  'mcworld',
  'mcpack',
  'mrpack',

  // Documents.
  'doc',
  'docx',
  'xls',
  'xlsx',
  'bmp',
]);

/**
 * The lowercased extension of a filename without its dot, or '' when there is none.
 *
 * A dotfile has no extension — `.gitignore` is a name — so the dot must not be at position 0. The
 * last dot wins, so `world.backup.tar.gz` is `gz`.
 */
export const attachmentExtension = (name: string): string => {
  const trimmed = name.trim();
  const dot = trimmed.lastIndexOf('.');
  if (dot <= 0 || dot === trimmed.length - 1) return '';

  return trimmed.slice(dot + 1).toLowerCase();
};

export const isAllowedAttachment = (name: string): boolean =>
  ALLOWED_ATTACHMENT_EXTENSIONS.has(attachmentExtension(name));

/**
 * The `accept` value for a file input, so the OS picker greys out what would be refused anyway.
 * A hint rather than a guard — the checks above still run, since a file can be dragged in.
 */
export const ATTACHMENT_ACCEPT = [...ALLOWED_ATTACHMENT_EXTENSIONS]
  .map((extension) => `.${extension}`)
  .join(',');
